import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../config/api';
import ConfirmationModal from '../components/ConfirmationModal';
import { ToastContext } from '../App';

interface Payment {
  id: number;
  month: string;
  amount: number;
  total_amount?: number;
  status: string;
  user_name?: string;
  user_email?: string;
  created_at?: string;
  paid_at?: string;
}

interface BookingSummary {
  user_id: number;
  user_name: string;
  user_email: string;
  total_lessons: number;
  animals: Array<{
    animal_name: string;
    lesson_count: number;
  }>;
}

interface PaymentStatistics {
  total_payments: number;
  total_amount: number;
  paid_payments: number;
  pending_payments: number;
  average_amount: number;
  monthly_breakdown: Array<{
    month: string;
    total_amount: number;
    payment_count: number;
  }>;
  statistics?: Array<any>;
}

const PaymentsPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('generate');
  const [loading, setLoading] = useState(false);
  const { showToast } = React.useContext(ToastContext);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Estados para Gerar Pagamentos
  const [paymentYear, setPaymentYear] = useState('');
  const [paymentMonth, setPaymentMonth] = useState('');
  const [bookingsSummary, setBookingsSummary] = useState<BookingSummary[]>([]);
  const [showBookingsSummary, setShowBookingsSummary] = useState(false);
  const [paymentAmounts, setPaymentAmounts] = useState<{ [key: number]: number }>({});

  // Estados para Gerir Pagamentos
  const [manageYear, setManageYear] = useState('');
  const [manageMonth, setManageMonth] = useState('');
  const [payments, setPayments] = useState<Payment[]>([]);

  // Estados para Pagamentos Pendentes
  const [pendingPayments, setPendingPayments] = useState<Payment[]>([]);
  const [pendingCount, setPendingCount] = useState(0);

  // Estados para Estatísticas
  const [statsStartYear, setStatsStartYear] = useState('');
  const [statsStartMonth, setStatsStartMonth] = useState('');
  const [statsEndYear, setStatsEndYear] = useState('');
  const [statsEndMonth, setStatsEndMonth] = useState('');
  const [paymentStatistics, setPaymentStatistics] = useState<any>(null);
  
  // Estados para modal de confirmação
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<number | null>(null);

  // Populate years
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const months = [
    { value: '01', label: 'Janeiro' },
    { value: '02', label: 'Fevereiro' },
    { value: '03', label: 'Março' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Maio' },
    { value: '06', label: 'Junho' },
    { value: '07', label: 'Julho' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' }
  ];

  useEffect(() => {
    // Set default values
    const now = new Date();
    const currentMonth = (now.getMonth() + 1).toString().padStart(2, '0');
    setPaymentYear(currentYear.toString());
    setPaymentMonth(currentMonth);
    setManageYear(currentYear.toString());
    setManageMonth(currentMonth);
    setStatsStartYear(currentYear.toString());
    setStatsStartMonth('01'); // Janeiro
    setStatsEndYear(currentYear.toString());
    setStatsEndMonth(currentMonth); // Mês corrente
    // Load initial data
    if (user?.role === 'trainer') {
      loadPaymentsByMonth();
      loadPendingPayments();
    }
  }, [user, currentYear]);

  // ===== GERAR PAGAMENTOS =====
  const handleGeneratePayments = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!paymentYear || !paymentMonth) {
      showToast('Selecione o ano e mês', 'error');
      return;
    }

      setLoading(true);

    try {
      const monthYear = `${paymentYear}-${paymentMonth}`;
      const data = await apiCall(`/payments/bookings-summary?month=${monthYear}`, { method: 'GET' });
      
      if (data.summary && data.summary.length > 0) {
        setBookingsSummary(data.summary);
        setShowBookingsSummary(true);
      } else {
        showToast('Nenhuma aula encontrada para este mês', 'info');
        setShowBookingsSummary(false);
      }
    } catch (err: any) {
      showToast(err.message || 'Erro ao carregar resumo de aulas', 'error');
      setShowBookingsSummary(false);
    } finally {
      setLoading(false);
    }
  };

  // ===== GERAR MENSALIDADES FIXAS =====
  const handleGenerateFixedMonthlyFees = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!paymentYear || !paymentMonth) {
      showToast('Selecione o ano e mês', 'error');
      return;
    }

    setLoading(true);

    try {
      const monthYear = `${paymentYear}-${paymentMonth}`;
      const data = await apiCall('/payments/generate-fixed-monthly-fees', {
        method: 'POST',
        body: JSON.stringify({ month: monthYear })
      });
      
      if (data.success) {
        showToast(data.message, 'success');
        // Recarregar listas
        loadPaymentsByMonth();
        loadPendingPayments();
      } else {
        showToast(data.error || 'Erro ao gerar mensalidades fixas', 'error');
      }
    } catch (err: any) {
      showToast(err.message || 'Erro ao gerar mensalidades fixas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentAmountChange = (userId: number, amount: string) => {
    setPaymentAmounts(prev => ({
      ...prev,
      [userId]: parseFloat(amount) || 0
    }));
  };

  const handleConfirmPayment = async (userId: number, monthYear: string) => {
    const amount = paymentAmounts[userId] || 0;
    
    setActionLoading(userId);
    try {
      if (amount === 0) {
        // Se o valor for 0, criar pagamento como pago diretamente
        await apiCall('/payments/generate', {
          method: 'POST',
          body: JSON.stringify({
            user_id: userId,
            month: monthYear,
            amount: 0
          })
        });
        showToast('Pagamento confirmado como pago (valor zero)', 'success');
      } else {
        // Primeiro criar pagamento pendente
        const createResponse = await apiCall('/payments/generate', {
          method: 'POST',
          body: JSON.stringify({
            user_id: userId,
            month: monthYear
          })
        });
        
        if (createResponse.success) {
          // Depois definir o valor e enviar notificação
          await apiCall('/payments/update-amount', {
            method: 'PUT',
            body: JSON.stringify({
              payment_id: createResponse.payment_id,
              amount: amount
            })
          });
          showToast('Pagamento criado e aluno notificado!', 'success');
        } else {
          throw new Error('Erro ao criar pagamento');
        }
      }
      
      // Limpar o valor
      setPaymentAmounts(prev => {
        const newAmounts = { ...prev };
        delete newAmounts[userId];
        return newAmounts;
      });
      // Atualizar bookingsSummary para remover o aluno da lista
      setBookingsSummary(prev => prev.filter(s => s.user_id !== userId));
      // Recarregar listas de pendentes e gerir pagamentos
      loadPaymentsByMonth();
      loadPendingPayments();
      
    } catch (err: any) {
      showToast(err.message || 'Erro ao confirmar pagamento', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // ===== GERIR PAGAMENTOS =====
  const loadPaymentsByMonth = async () => {
    setLoading(true);
    try {
      let url = '/payments';
      const params = new URLSearchParams();
      
      if (manageYear && manageMonth) {
        const monthParam = `${manageYear}-${manageMonth}`;
        url += `?month=${monthParam}`;
      }
      
      const data = await apiCall(url, { method: 'GET' });
      setPayments(data.payments || []);
    } catch (err: any) {
      showToast(err.message || 'Erro ao carregar pagamentos', 'error');
      } finally {
        setLoading(false);
      }
    };

  const clearPaymentFilters = () => {
    setManageYear('');
    setManageMonth('');
    loadPaymentsByMonth();
  };

  const handleMarkAsPaid = async (paymentId: number) => {
    setActionLoading(paymentId);
    try {
      await apiCall('/payments/mark-paid', {
        method: 'PUT',
        body: JSON.stringify({ payment_id: paymentId })
      });
      
      setPayments(prev => prev.map(p => 
        p.id === paymentId ? { ...p, status: 'paid' } : p
      ));
      
        showToast('Pagamento marcado como pago!', 'success');
      loadPendingPayments(); // Recarregar contagem
    } catch (err: any) {
      showToast(err.message || 'Erro ao marcar como pago', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const showDeleteConfirmation = (paymentId: number) => {
    setPaymentToDelete(paymentId);
    setShowDeleteModal(true);
  };

  const handleDeletePayment = async () => {
    if (!paymentToDelete) return;
    
    setActionLoading(paymentToDelete);
    try {
      await apiCall('/payments', {
        method: 'DELETE',
        body: JSON.stringify({ payment_id: paymentToDelete })
      });
      
      setPayments(prev => prev.filter(p => p.id !== paymentToDelete));
      showToast('Pagamento eliminado com sucesso!', 'success');
      loadPendingPayments(); // Recarregar contagem
      setShowDeleteModal(false);
      setPaymentToDelete(null);
    } catch (err: any) {
      showToast(err.message || 'Erro ao eliminar pagamento', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // ===== PAGAMENTOS PENDENTES =====
  const loadPendingPayments = async () => {
    try {
      const data = await apiCall('/payments/pending', { method: 'GET' });
      setPendingPayments(data.pending_payments || []);
      setPendingCount(data.pending_payments?.length || 0);
    } catch (err: any) {
      console.error('Erro ao carregar pagamentos pendentes:', err);
    }
  };

  // ===== ESTATÍSTICAS =====
  const loadPaymentStatistics = async () => {
    if (!statsStartYear || !statsStartMonth || !statsEndYear || !statsEndMonth) {
      showToast('Selecione o período completo', 'error');
      return;
    }

    setLoading(true);
    try {
      const startMonth = `${statsStartYear}-${statsStartMonth}`;
      const endMonth = `${statsEndYear}-${statsEndMonth}`;
      const data = await apiCall(`/payments/statistics?start_month=${startMonth}&end_month=${endMonth}`, { method: 'GET' });
      setPaymentStatistics(data);
    } catch (err: any) {
      showToast(err.message || 'Erro ao carregar estatísticas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const clearStatisticsFilters = () => {
    setStatsStartYear(currentYear.toString());
    setStatsStartMonth('');
    setStatsEndYear(currentYear.toString());
    setStatsEndMonth('');
    setPaymentStatistics(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatMonth = (monthYear: string) => {
    const [year, month] = monthYear.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  // Calcular totais a partir do array statistics
  const statisticsArray = paymentStatistics && Array.isArray(paymentStatistics.statistics) ? paymentStatistics.statistics : [];
  const totalPagamentos = statisticsArray.reduce((sum: number, stat: any) => sum + Number(stat.total_payments || 0), 0);
  const totalValor = statisticsArray.reduce((sum: number, stat: any) => sum + Number(stat.total_amount || 0), 0);
  const totalPagos = statisticsArray.reduce((sum: number, stat: any) => sum + Number(stat.paid_payments || 0), 0);
  const totalMedio = statisticsArray.length > 0 ? totalValor / statisticsArray.length : 0;

  if (user?.role !== 'trainer') {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Acesso restrito a treinadores</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow mb-4">
            <div className="card-header bg-warning text-dark">
              <h5><i className="fas fa-credit-card me-2"></i>Gestão de Pagamentos</h5>
            </div>
            <div className="card-body">
              {/* Tabs */}
              <ul className="nav nav-tabs w-100" role="tablist">
                <li className="nav-item" role="presentation">
                  <button 
                    className={`nav-link w-100 ${activeTab === 'generate' ? 'active' : ''}`}
                    onClick={() => setActiveTab('generate')}
                  >
                    <i className="fas fa-plus me-1"></i>Gerar Pagamentos
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button 
                    className={`nav-link w-100 ${activeTab === 'manage' ? 'active' : ''}`}
                    onClick={() => setActiveTab('manage')}
                  >
                    <i className="fas fa-list me-1"></i>Gerir Pagamentos
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button 
                    className={`nav-link w-100 ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending')}
                  >
                    <i className="fas fa-clock me-1"></i>Pendentes
                    <span className="badge bg-warning text-dark ms-1">{pendingCount}</span>
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button 
                    className={`nav-link w-100 ${activeTab === 'statistics' ? 'active' : ''}`}
                    onClick={() => setActiveTab('statistics')}
                  >
                    <i className="fas fa-chart-line me-1"></i>Estatísticas
                  </button>
                </li>
              </ul>

              <div className="tab-content mt-3">
                {/* Tab Gerar Pagamentos */}
                {activeTab === 'generate' && (
                  <div className="tab-pane fade show active">
                    <div className="row">
                      <div className="col-md-6">
                        <h6 className="text-warning mb-3">
                          <i className="fas fa-calendar-plus me-2"></i>Gerar Pagamentos Mensais
                        </h6>
                        <form onSubmit={handleGeneratePayments}>
                          <div className="row">
                            <div className="col-md-6">
                              <label htmlFor="paymentYear" className="form-label">Ano:</label>
                              <select 
                                className="form-select" 
                                id="paymentYear"
                                value={paymentYear}
                                onChange={(e) => setPaymentYear(e.target.value)}
                                required
                              >
                                <option value="">Selecionar ano</option>
                                {years.map(year => (
                                  <option key={year} value={year}>{year}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="paymentMonth" className="form-label">Mês:</label>
                              <select 
                                className="form-select" 
                                id="paymentMonth"
                                value={paymentMonth}
                                onChange={(e) => setPaymentMonth(e.target.value)}
                                required
                              >
                                <option value="">Selecionar mês</option>
                                {months.map(month => (
                                  <option key={month.value} value={month.value}>{month.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="mt-3">
                            <button type="submit" className="btn btn-warning w-100" disabled={loading}>
                              {loading ? 'A carregar...' : (
                                <>
                                  <i className="fas fa-search me-1"></i>Ver Aulas do Mês
                                </>
                              )}
                            </button>
                          </div>
                        </form>
                        
                        <div className="mt-3">
                          <button 
                            type="button" 
                            className="btn btn-info w-100" 
                            onClick={handleGenerateFixedMonthlyFees}
                            disabled={loading || !paymentYear || !paymentMonth}
                          >
                            {loading ? 'A carregar...' : (
                              <>
                                <i className="fas fa-euro-sign me-1"></i>Gerar Mensalidades Fixas
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="alert alert-info">
                          <h6><i className="fas fa-info-circle me-2"></i>Como funciona:</h6>
                          <ul className="mb-0">
                            <li><strong>Ver Aulas do Mês:</strong> Para alunos sem mensalidade fixa</li>
                            <li><strong>Gerar Mensalidades Fixas:</strong> Para alunos com mensalidade fixa configurada</li>
                            <li>Selecione o ano e mês desejados</li>
                            <li>Defina os valores para cada aluno (se aplicável)</li>
                            <li>Confirme os pagamentos para enviar emails</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Resumo de aulas */}
                    {showBookingsSummary && (
                      <div className="mt-4">
                        <h6 className="text-warning mb-3">
                          <i className="fas fa-list me-2"></i>Resumo de Aulas por Aluno
                        </h6>
                        <div className="row">
                          {bookingsSummary.map((student) => {
                            const monthYear = `${paymentYear}-${paymentMonth}`;
                            const monthName = formatMonth(monthYear);
                            
                            return (
                              <div key={student.user_id} className="col-12 mb-3">
                                <div className="card">
                                  <div className="card-body">
                                    <div className="row align-items-center">
                                      <div className="col-md-3">
                                        <h6 className="mb-1">{student.user_name}</h6>
                                        <small className="text-muted">{student.user_email}</small>
                                        <div className="mt-2">
                                          <strong className="text-primary">
                                            Total: {student.total_lessons} aula{student.total_lessons > 1 ? 's' : ''}
                                          </strong>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <ul className="list-unstyled mb-0">
                                          {student.animals.map((animal, index) => (
                                            <li key={index}>
                                              <strong>{animal.animal_name}:</strong> {animal.lesson_count} aula{animal.lesson_count > 1 ? 's' : ''}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                      <div className="col-md-3 mt-2">
                                        <div className="input-group">
                                          <span className="input-group-text">€</span>
                                          <input 
                                            type="number" 
                                            className="form-control"
                                            step="0.01" 
                                            min="0" 
                                            placeholder="0.00"
                                            value={paymentAmounts[student.user_id] || ''}
                                            onInput={e => {
                                              const val = (e.target as HTMLInputElement).value;
                                              // Permitir apenas números e ponto
                                              if (/^\d*\.?\d*$/.test(val)) {
                                                handlePaymentAmountChange(student.user_id, val);
                                              }
                                            }}
                                          />
                                        </div>
                                      </div>
                                      <div className="col-md-3 col-12 mt-2 mt-md-0">
                                        <button 
                                          className="btn btn-success btn-sm w-100"
                                          onClick={() => handleConfirmPayment(student.user_id, monthYear)}
                                          disabled={actionLoading === student.user_id}
                                        >
                                          {actionLoading === student.user_id ? (
                                            'A processar...'
                                          ) : (
                                            <>
                                              <i className="fas fa-check me-1"></i>Confirmar
                                            </>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tab Gerir Pagamentos */}
                {activeTab === 'manage' && (
                  <div className="tab-pane fade show active">
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label htmlFor="manageYear" className="form-label">Ano:</label>
                        <select 
                          className="form-select" 
                          id="manageYear"
                          value={manageYear}
                          onChange={(e) => setManageYear(e.target.value)}
                        >
                          <option value="">Todos os anos</option>
                          {years.map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label htmlFor="manageMonth" className="form-label">Mês:</label>
                        <select 
                          className="form-select" 
                          id="manageMonth"
                          value={manageMonth}
                          onChange={(e) => setManageMonth(e.target.value)}
                        >
                          <option value="">Todos os meses</option>
                          {months.map(month => (
                            <option key={month.value} value={month.value}>{month.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3 col-12 d-flex align-items-end mt-2">
                        <button className="btn w-100" style={{ backgroundColor: '#a2449c', color: 'white' }} onClick={loadPaymentsByMonth} disabled={loading}>
                          <i className="fas fa-search me-1"></i>Carregar
                        </button>
                      </div>
                      <div className="col-md-3 d-flex align-items-end justify-content-end mt-2">
                        <button className="btn w-100 btn-outline-secondary" onClick={clearPaymentFilters}>
                          <i className="fas fa-times me-1"></i>Limpar Filtros
                        </button>
                      </div>
                    </div>

                    {loading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">A carregar...</span>
                        </div>
                      </div>
                    ) : (
        <div className="table-responsive">
          <table className="table table-bordered align-middle">
            <thead>
              <tr>
                <th>Mês</th>
                              <th>Aluno</th>
                <th>Valor</th>
                <th>Estado</th>
                              <th>Data Criação</th>
                              <th>Ações</th>
              </tr>
            </thead>
            <tbody>
                            {payments.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="text-center">Nenhum pagamento encontrado.</td>
                              </tr>
                            ) : (
                              payments.map(payment => (
                <tr key={payment.id}>
                                  <td>{formatMonth(payment.month)}</td>
                                  <td>{payment.user_name}</td>
                  <td>{
                    payment.total_amount !== undefined
                      ? Number(payment.total_amount).toFixed(2)
                      : (typeof payment.amount === 'number' ? payment.amount.toFixed(2) : '0.00')
                  } €</td>
                                  <td>
                                    {payment.status === 'paid' ? (
                                      <span className="badge bg-success">Pago</span>
                                    ) : (
                                      <span className="badge bg-warning text-dark">Pendente</span>
                                    )}
                                  </td>
                                  <td>{payment.created_at ? formatDate(payment.created_at) : '-'}</td>
                    <td>
                      <div className="d-flex gap-2">
                        {payment.status !== 'paid' && (
                          <button
                            className="btn btn-sm btn-success"
                                          onClick={() => handleMarkAsPaid(payment.id)}
                            disabled={actionLoading === payment.id}
                          >
                            {actionLoading === payment.id ? 'A processar...' : 'Marcar como Pago'}
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => showDeleteConfirmation(payment.id)}
                          disabled={actionLoading === payment.id}
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Tab Pagamentos Pendentes */}
                {activeTab === 'pending' && (
                  <div className="tab-pane fade show active">
                    <h6 className="text-warning mb-3">
                      <i className="fas fa-clock me-2"></i>Pagamentos Pendentes de Confirmação
                    </h6>
                    
                    {(Array.isArray(pendingPayments) ? pendingPayments : []).length === 0 ? (
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle me-2"></i>
                        Não há pagamentos pendentes.
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-bordered align-middle">
                          <thead>
                            <tr>
                              <th>Mês</th>
                              <th>Aluno</th>
                              <th>Email</th>
                              <th>Valor</th>
                              <th>Data Criação</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(Array.isArray(pendingPayments) ? pendingPayments : []).map(payment => (
                              <tr key={payment.id}>
                                <td>{formatMonth(payment.month)}</td>
                                <td>{payment.user_name}</td>
                                <td>{payment.user_email}</td>
                                <td>{Number(payment.total_amount).toFixed(2)} €</td>
                                <td>{payment.created_at ? formatDate(payment.created_at) : '-'}</td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-success"
                                      onClick={() => handleMarkAsPaid(payment.id)}
                                      disabled={actionLoading === payment.id}
                                    >
                                      {actionLoading === payment.id ? 'A processar...' : 'Marcar como Pago'}
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => showDeleteConfirmation(payment.id)}
                                      disabled={actionLoading === payment.id}
                                    >
                                      Eliminar
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
            </tbody>
          </table>
        </div>
      )}
                  </div>
                )}

                {/* Tab Estatísticas de Pagamentos */}
                {activeTab === 'statistics' && (
                  <div className="tab-pane fade show active">
                    <div className="row mb-3">
                      <div className="col-md-2">
                        <label htmlFor="statsStartYear" className="form-label">Ano Início:</label>
                        <select 
                          className="form-select" 
                          id="statsStartYear"
                          value={statsStartYear}
                          onChange={(e) => setStatsStartYear(e.target.value)}
                        >
                          <option value="">Selecionar ano</option>
                          {years.map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label htmlFor="statsStartMonth" className="form-label">Mês Início:</label>
                        <select 
                          className="form-select" 
                          id="statsStartMonth"
                          value={statsStartMonth}
                          onChange={(e) => setStatsStartMonth(e.target.value)}
                        >
                          <option value="">Selecionar mês</option>
                          {months.map(month => (
                            <option key={month.value} value={month.value}>{month.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label htmlFor="statsEndYear" className="form-label">Ano Fim:</label>
                        <select 
                          className="form-select" 
                          id="statsEndYear"
                          value={statsEndYear}
                          onChange={(e) => setStatsEndYear(e.target.value)}
                        >
                          <option value="">Selecionar ano</option>
                          {years.map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label htmlFor="statsEndMonth" className="form-label">Mês Fim:</label>
                        <select 
                          className="form-select" 
                          id="statsEndMonth"
                          value={statsEndMonth}
                          onChange={(e) => setStatsEndMonth(e.target.value)}
                        >
                          <option value="">Selecionar mês</option>
                          {months.map(month => (
                            <option key={month.value} value={month.value}>{month.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2 col-12 d-flex align-items-end mt-2">
                        <button className="btn w-100" style={{ backgroundColor: '#a2449c', color: 'white' }} onClick={loadPaymentStatistics} disabled={loading}>
                          {loading ? 'A carregar...' : (
                            <>
                              <i className="fas fa-chart-bar me-1"></i>Carregar
                            </>
                          )}
                        </button>
                      </div>
                      <div className="col-md-2 d-flex align-items-end justify-content-end mt-2">
                        <button className="btn w-100 btn-outline-secondary" onClick={clearStatisticsFilters}>
                          <i className="fas fa-times me-1"></i>Limpar
                        </button>
                      </div>
                    </div>

                    {paymentStatistics && (
                      <div className="row">
                        <div className="col-md-3">
                          <div className="card text-center">
                            <div className="card-body">
                              <h5 className="card-title">Total Pagamentos</h5>
                              <div className="display-6 text-primary">{totalPagamentos}</div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="card text-center">
                            <div className="card-body">
                              <h5 className="card-title">Valor Total</h5>
                              <div className="display-6 text-success">{totalValor.toFixed(2)} €</div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="card text-center">
                            <div className="card-body">
                              <h5 className="card-title">Pagamentos Pagos</h5>
                              <div className="display-6 text-info">{totalPagos}</div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="card text-center">
                            <div className="card-body">
                              <h5 className="card-title">Valor Médio</h5>
                              <div className="display-6 text-warning">{totalMedio.toFixed(2)} €</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Tabela mensal de estatísticas */}
                    {paymentStatistics && Array.isArray(paymentStatistics.statistics) && paymentStatistics.statistics.length > 0 && (
                      <div className="table-responsive mt-4">
                        <table className="table table-striped">
                          <thead className="table-secondary">
                            <tr>
                              <th className="text-start">Mês</th>
                              <th className="text-end">Total Pagamentos</th>
                              <th className="text-end">Pendentes</th>
                              <th className="text-end">Pagos</th>
                              <th className="text-end">Valor Total</th>
                              <th className="text-end">Valor Pendente</th>
                              <th className="text-end">Valor Pago</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paymentStatistics.statistics.map((stat: any) => (
                              <tr key={stat.month}>
                                <td className="text-start">{formatMonth(stat.month)}</td>
                                <td className="text-end">{stat.total_payments}</td>
                                <td className="text-end"><span className="badge bg-warning">{stat.pending_payments}</span></td>
                                <td className="text-end"><span className="badge bg-success">{stat.paid_payments}</span></td>
                                <td className="text-end payment-value"><strong>{Number(stat.total_amount).toFixed(2)}€</strong></td>
                                <td className="text-end payment-value text-warning">{Number(stat.pending_amount).toFixed(2)}€</td>
                                <td className="text-end payment-value text-success">{Number(stat.paid_amount).toFixed(2)}€</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {/* The ToastContext handles its own rendering */}

      {/* Modal de Confirmação para Eliminar Pagamento */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Eliminar Pagamento"
        message="Tem a certeza que deseja eliminar este pagamento?"
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmButtonClass="btn-danger"
        onConfirm={handleDeletePayment}
        onCancel={() => {
          setShowDeleteModal(false);
          setPaymentToDelete(null);
        }}
        isLoading={actionLoading !== null}
      />
    </div>
  );
};

export default PaymentsPage; 